import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import dayjs from "dayjs";
import { useUpdateRegistrationPricingMutation } from "../../features/eventRegistrations/eventRegistrationsApiSlice";
import {
  calculateBookingPricePreview,
  getSortedSessions,
} from "./booking/bookingPricingUtils";

const formatEur = (v) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(v ?? 0);

export default function EditRegistrationPricingDialog({
  open,
  onClose,
  registration,
  event,
}) {
  const [updatePricing, { isLoading }] = useUpdateRegistrationPricingMutation();

  // ── Mode: "recalc" or "override" ─────────────────────────────────────
  const [mode, setMode] = useState("recalc");

  // ── Recalc mode state ─────────────────────────────────────────────────
  const [selectedRuleId, setSelectedRuleId] = useState("");
  const [selectedSessionIds, setSelectedSessionIds] = useState([]);
  const [discountLabel, setDiscountLabel] = useState("");
  const [discountType, setDiscountType] = useState("fixed");
  const [discountValue, setDiscountValue] = useState("");
  const [hasDiscount, setHasDiscount] = useState(false);

  // ── Override mode state ───────────────────────────────────────────────
  const [overrideAmount, setOverrideAmount] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  // ── Error / success ───────────────────────────────────────────────────
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const closeTimerRef = useRef(null);

  // ── Initialize from registration data ─────────────────────────────────
  useEffect(() => {
    clearTimeout(closeTimerRef.current);
    if (!registration || !open) return;

    const hasOverride = registration.amountOverride?.amount != null;
    setMode(hasOverride ? "override" : "recalc");

    setSelectedRuleId(registration.selectedPricingRuleId || "");
    setSelectedSessionIds(registration.selectedSessionIds || []);

    const ad = registration.adminDiscount;
    if (ad?.type && ad?.value != null) {
      setHasDiscount(true);
      setDiscountLabel(ad.label || "");
      setDiscountType(ad.type);
      setDiscountValue(String(ad.value));
    } else {
      setHasDiscount(false);
      setDiscountLabel("");
      setDiscountType("fixed");
      setDiscountValue("");
    }

    if (hasOverride) {
      setOverrideAmount(String(registration.amountOverride.amount));
      setOverrideReason(registration.amountOverride.reason || "");
    } else {
      setOverrideAmount("");
      setOverrideReason("");
    }

    setError("");
    setSuccess("");
  }, [registration, open]);

  // ── Sorted sessions from event ────────────────────────────────────────
  const sortedSessions = useMemo(
    () => getSortedSessions(event?.sessions || []),
    [event?.sessions],
  );

  // ── Live price preview (recalc mode) ──────────────────────────────────
  const pricePreview = useMemo(() => {
    if (mode !== "recalc" || !event || !selectedRuleId) return null;

    const adminDisc = hasDiscount
      ? {
          type: discountType,
          value: parseFloat(discountValue) || 0,
          label: discountLabel,
        }
      : undefined;

    return calculateBookingPricePreview({
      event,
      selectedPricingRuleId: selectedRuleId,
      selectedSessionIds,
      participantCount: (registration?.participants || []).length || 1,
      memberParticipantCount: (registration?.participants || []).filter(
        (p) => p.memberRef,
      ).length,
      bookingDate: registration?.createdAt
        ? new Date(registration.createdAt)
        : new Date(),
      adminDiscount: adminDisc,
    });
  }, [
    mode,
    event,
    selectedRuleId,
    selectedSessionIds,
    hasDiscount,
    discountType,
    discountValue,
    discountLabel,
    registration,
  ]);

  // ── Session toggle ────────────────────────────────────────────────────
  const toggleSession = (sessionId) => {
    setSelectedSessionIds((prev) =>
      prev.includes(sessionId)
        ? prev.filter((id) => id !== sessionId)
        : [...prev, sessionId],
    );
  };

  // ── Price history from audit log ──────────────────────────────────────
  const priceHistory = useMemo(() => {
    if (!registration?.auditLog) return [];
    return registration.auditLog
      .filter((entry) => entry.action === "pricing_changed")
      .sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt));
  }, [registration?.auditLog]);

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError("");
    setSuccess("");

    try {
      const body = {};
      const hadOverride = registration.amountOverride?.amount != null;

      if (mode === "override") {
        const amt = parseFloat(overrideAmount);
        if (isNaN(amt) || amt < 0) {
          setError("Bitte einen gültigen Betrag (>= 0) eingeben.");
          return;
        }
        if (!overrideReason.trim()) {
          setError("Bitte einen Grund für den Festpreis angeben.");
          return;
        }
        body.amountOverride = {
          amount: amt,
          reason: overrideReason.trim(),
        };
      } else {
        // recalc mode
        if (hadOverride) {
          body.removeAmountOverride = true;
        }
        if (selectedRuleId !== registration.selectedPricingRuleId) {
          body.selectedPricingRuleId = selectedRuleId;
        }
        const origSessions = registration.selectedSessionIds || [];
        const sessionsEqual =
          selectedSessionIds.length === origSessions.length &&
          selectedSessionIds.every((id) => origSessions.includes(id));
        if (!sessionsEqual) {
          body.selectedSessionIds = selectedSessionIds;
        }
        if (hasDiscount) {
          const val = parseFloat(discountValue) || 0;
          if (val > 0) {
            body.adminDiscount = {
              label: discountLabel || "Admin-Rabatt",
              type: discountType,
              value: val,
            };
          } else if (registration.adminDiscount?.value != null) {
            body.removeAdminDiscount = true;
          }
        } else if (registration.adminDiscount?.value != null) {
          body.removeAdminDiscount = true;
        }

        // If no explicit field changed but the recalculated price differs
        // from the current total (e.g., pricing rule amount was updated on
        // the event), force a server-side recalculation by sending the
        // current ruleId.
        if (!hadOverride && Object.keys(body).length === 0) {
          const regTotal = registration.totalAmount || 0;
          if (pricePreview && pricePreview.totalAmount !== regTotal) {
            body.selectedPricingRuleId = selectedRuleId;
          } else {
            setError("Keine Änderungen erkannt.");
            return;
          }
        }
      }

      const result = await updatePricing({
        eventId: event._id || event.id,
        registrationId: registration._id || registration.id,
        ...body,
      }).unwrap();

      setSuccess(
        `Preis aktualisiert: ${formatEur(result.registration?.totalAmount)}`,
      );
      closeTimerRef.current = setTimeout(() => onClose?.(), 1200);
    } catch (err) {
      setError(
        err?.data?.message || err?.message || "Fehler beim Speichern.",
      );
    }
  };

  if (!registration || !event) return null;

  const currentTotal = registration.totalAmount || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Preis bearbeiten</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* ── Aktuelle Preisinfo ──────────────────────────────────── */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Aktuelle Preisinfo
            </Typography>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                {registration.pricingSnapshot?.ruleLabelAtBooking || "–"}
                {registration.pricingSnapshot?.ruleTypeAtBooking
                  ? ` (${registration.pricingSnapshot.ruleTypeAtBooking})`
                  : ""}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatEur(currentTotal)}
                {registration.amountOverride?.amount != null && (
                  <Chip
                    label="Festpreis"
                    size="small"
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
            </Stack>
            {sortedSessions.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Sessions: {(registration.selectedSessionIds || []).length} von{" "}
                {sortedSessions.length} gewählt
              </Typography>
            )}
          </Box>

          <Divider />

          {/* ── Bearbeitungsmodus ───────────────────────────────────── */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Bearbeitungsmodus
            </Typography>
            <RadioGroup
              value={mode}
              onChange={(e) => setMode(e.target.value)}
            >
              <FormControlLabel
                value="recalc"
                control={<Radio size="small" />}
                label="Preisregel & Sitzungen ändern"
              />
              <FormControlLabel
                value="override"
                control={<Radio size="small" />}
                label="Festpreis setzen"
              />
            </RadioGroup>
          </Box>

          <Divider />

          {/* ── Recalc mode ────────────────────────────────────────── */}
          {mode === "recalc" && (
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Preisregel</InputLabel>
                <Select
                  value={selectedRuleId}
                  label="Preisregel"
                  onChange={(e) => setSelectedRuleId(e.target.value)}
                >
                  {(event.pricingRules || []).map((rule) => (
                    <MenuItem key={rule.ruleId} value={rule.ruleId}>
                      {rule.label} — {formatEur(rule.amount)}{" "}
                      ({rule.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {sortedSessions.length > 0 && (
                <Box>
                  <Typography variant="body2" gutterBottom>
                    Sitzungen
                  </Typography>
                  <Stack spacing={0.5}>
                    {sortedSessions.map((session) => (
                      <FormControlLabel
                        key={session.sessionId}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedSessionIds.includes(
                              session.sessionId,
                            )}
                            onChange={() =>
                              toggleSession(session.sessionId)
                            }
                          />
                        }
                        label={`${session.label}${
                          session.date
                            ? ` (${dayjs(session.date).format("DD.MM.YYYY")})`
                            : ""
                        }`}
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={hasDiscount}
                      onChange={(e) => setHasDiscount(e.target.checked)}
                    />
                  }
                  label="Admin-Rabatt"
                />
                {hasDiscount && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      label="Bezeichnung"
                      value={discountLabel}
                      onChange={(e) => setDiscountLabel(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Typ</InputLabel>
                      <Select
                        value={discountType}
                        label="Typ"
                        onChange={(e) => setDiscountType(e.target.value)}
                      >
                        <MenuItem value="fixed">Betrag (€)</MenuItem>
                        <MenuItem value="percentage">Prozent (%)</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Wert"
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      sx={{ width: 100 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {discountType === "percentage" ? "%" : "€"}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                )}
              </Box>

              {/* Preview */}
              {pricePreview && (
                <Alert
                  severity={
                    pricePreview.totalAmount !== currentTotal
                      ? "info"
                      : "success"
                  }
                  icon={<Icon icon="mdi:calculator" width={20} />}
                >
                  <Typography variant="body2">
                    <strong>Vorschau:</strong> Neu:{" "}
                    {formatEur(pricePreview.totalAmount)} (vorher:{" "}
                    {formatEur(currentTotal)})
                  </Typography>
                </Alert>
              )}
            </Stack>
          )}

          {/* ── Override mode ──────────────────────────────────────── */}
          {mode === "override" && (
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Betrag"
                type="number"
                value={overrideAmount}
                onChange={(e) => setOverrideAmount(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">€</InputAdornment>
                  ),
                }}
                fullWidth
              />
              <TextField
                size="small"
                label="Grund"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                fullWidth
                required
                placeholder="z.B. Sondervereinbarung, Kulanz..."
              />
              {overrideAmount !== "" && (
                <Alert
                  severity={
                    parseFloat(overrideAmount) !== currentTotal
                      ? "info"
                      : "success"
                  }
                  icon={<Icon icon="mdi:calculator" width={20} />}
                >
                  <Typography variant="body2">
                    <strong>Vorschau:</strong> Neu:{" "}
                    {formatEur(parseFloat(overrideAmount) || 0)} (vorher:{" "}
                    {formatEur(currentTotal)})
                  </Typography>
                </Alert>
              )}
            </Stack>
          )}

          {/* ── Preishistorie ──────────────────────────────────────── */}
          {priceHistory.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Preishistorie
                </Typography>
                <Stack spacing={0.5}>
                  {priceHistory.map((entry, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      color="text.secondary"
                    >
                      {dayjs(entry.performedAt).format("DD.MM.YYYY HH:mm")}
                      {" — "}
                      {entry.details}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Original booking info */}
          {registration.pricingSnapshot?.snapshotAt && (
            <Typography variant="body2" color="text.secondary">
              Erstbuchung:{" "}
              {dayjs(registration.pricingSnapshot.snapshotAt).format(
                "DD.MM.YYYY",
              )}{" "}
              — {formatEur(registration.pricingSnapshot.totalAmount)}
            </Typography>
          )}

          {/* ── Error / Success ────────────────────────────────────── */}
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isLoading || Boolean(success)}
        >
          {isLoading ? "Speichert..." : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EditRegistrationPricingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  registration: PropTypes.object,
  event: PropTypes.object,
};
